export type WhatsAppConnectionStatus =
  | 'DISCONNECTED'
  | 'CONNECTING'
  | 'CONNECTED'
  | 'QR_READY';

export interface WhatsAppStatusResponse {
  status: WhatsAppConnectionStatus;
  qr: string | null;
  hasStoredCredentials: boolean;
  storedSessionCount: number;
  connectedNumber: string | null;
  connectedName: string | null;
}

/**
 * Rol de un número en el pool. El 'registro' es el número base de Aguacate
 * (id 'base'); los 'pagador' son extras que solo mandan OTP de pagadores
 * (marca blanca), así los baneos o caídas de uno no afectan al otro.
 */
export type WhatsAppNumberRole = 'registro' | 'pagador';

/** Id fijo con el que el backend identifica al número base (registro). */
export const BASE_WHATSAPP_NUMBER_ID = 'base';

export interface WhatsAppNumber {
  id: string;
  label: string;
  role: WhatsAppNumberRole;
  status: WhatsAppConnectionStatus;
  connectedNumber: string | null;
  connectedName: string | null;
  hasStoredCredentials: boolean;
  messagesThisMinute: number;
  createdAt: string | null;
}

/** Detalle de un número: lo mismo que la lista más el QR (solo en QR_READY). */
export interface WhatsAppNumberStatus extends WhatsAppNumber {
  qr: string | null;
}

export interface AddWhatsAppNumberRequest {
  label: string;
}

export interface WhatsAppConnectResponse {
  success: boolean;
  message: string;
  status: WhatsAppConnectionStatus;
}

export interface WhatsAppDisconnectResponse {
  success: boolean;
  message: string;
}

export interface RateLimitStatusResponse {
  messagesThisMinute: number;
  maxPerMinute: number;
  resetInMs: number;
}

export interface OTPStatsResponse {
  pending: number;
  verified: number;
  expired: number;
  blocked: number;
  numbersInCache: number;
}

export interface CleanupCacheResponse {
  success: boolean;
  deletedFromCache: number;
  expiredOTPs: number;
  message: string;
}

export type OTPStatusType = 'PENDING' | 'VERIFIED' | 'EXPIRED' | 'BLOCKED';

/** Para qué se pidió el OTP: registro de usuarios de Aguacate o pagador. */
export type OTPPurpose = 'registro' | 'pagador';

export interface OTPItem {
  otpId: number;
  phoneNumber: string;
  status: OTPStatusType;
  attempts: number;
  maxAttempts: number;
  expiresAt: string;
  verifiedAt: string | null;
  createdAt: string;
  purpose: OTPPurpose;
  /** Número que lo envió: 'base' o el id del extra. null si no llegó a salir. */
  sender: string | null;
}

export interface OTPListResponse {
  items: OTPItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface OTPListQuery {
  page?: number;
  limit?: number;
  status?: OTPStatusType;
  phoneNumber?: string;
}

export interface SendTestOTPRequest {
  phoneNumber: string;
  /** 'base' (por defecto) o el id de un número extra. */
  numberId?: string;
  /** Solo extras: nombre de cliente de ejemplo para el encabezado. */
  senderName?: string;
}

export type SendOTPResultCode =
  | 'SENT'
  | 'COOLDOWN'
  | 'RATE_LIMITED'
  | 'UNAVAILABLE'
  | 'NOT_ON_WHATSAPP'
  | 'SEND_FAILED'
  | 'ERROR';

export interface SendTestOTPResponse {
  success: boolean;
  /** Resultado estable para decidir qué mostrar (no parsear `message`). */
  code?: SendOTPResultCode;
  message: string;
  expiresAt?: string;
  /** Vida restante del código; preferir sobre expiresAt (no depende del reloj local). */
  expiresInSeconds?: number;
  /** Segundos que faltan para poder reenviar al mismo número (cooldown anti-ban). */
  resendInSeconds?: number;
}
